import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ArrowLeft as ArrowLeftIcon } from "@phosphor-icons/react/ArrowLeft";
import { ArrowRight as ArrowRightIcon } from "@phosphor-icons/react/ArrowRight";

export interface SmallPaginationProps {
	/** Current page (0-indexed) */
	page: number;
	/** Total number of pages */
	totalPages: number;
	/** Callback when page changes */
	onPageChange: (page: number) => void;
	/** Compact mode for smaller spacing */
	compact?: boolean;
}

export function SmallPagination({
	page,
	totalPages,
	onPageChange,
	compact = false,
}: SmallPaginationProps): React.JSX.Element {
	const currentPage = page + 1; // Convert to 1-indexed for display

	const handlePrevious = () => {
		if (page > 0) {
			onPageChange(page - 1);
		}
	};

	const handleNext = () => {
		if (page < totalPages - 1) {
			onPageChange(page + 1);
		}
	};

	const isPrevDisabled = page <= 0;
	const isNextDisabled = page >= totalPages - 1;

	if (totalPages <= 1) {
		return <Box />; // Don't show pagination if only one page
	}

	return (
		<Box
			sx={{ display: "flex", justifyContent: "center", py: compact ? 1 : 2 }}>
			<Card
				sx={{
					boxShadow: compact ? "var(--mui-shadows-8)" : "var(--mui-shadows-16)",
				}}>
				<Stack
					direction="row"
					spacing={compact ? 2 : 3}
					sx={{ alignItems: "center", p: compact ? 0.75 : 1 }}>
					<Button
						color="secondary"
						size="small"
						startIcon={<ArrowLeftIcon size={16} />}
						onClick={handlePrevious}
						disabled={isPrevDisabled}
						sx={{
							minWidth: "auto",
							fontSize: compact ? "0.75rem" : "0.875rem",
						}}>
						Prev
					</Button>
					<Typography
						color="text.secondary"
						variant="subtitle2"
						sx={{
							fontSize: compact ? "0.75rem" : "0.875rem",
							fontWeight: "medium",
							minWidth: "auto",
						}}>
						{currentPage}/{totalPages}
					</Typography>
					<Button
						color="secondary"
						size="small"
						endIcon={<ArrowRightIcon size={16} />}
						onClick={handleNext}
						disabled={isNextDisabled}
						sx={{
							minWidth: "auto",
							fontSize: compact ? "0.75rem" : "0.875rem",
						}}>
						Next
					</Button>
				</Stack>
			</Card>
		</Box>
	);
}
